import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
    IconArrowLeft, IconLoader2, IconHistory, IconMessage, IconLock,
    IconPaperclip, IconAlertTriangle, IconUserCheck,
} from '@tabler/icons-react';
import { useAuth } from '../../contexts/AuthContext';
import Breadcrumb from '../../components/common/Breadcrumb';
import { PageLoader } from '../../components/common/Loader';
import { useUsers } from '../../hooks/useUsers';
import {
    useSupportTicket, useUpdateTicket, useTicketComments, useAddTicketComment,
    useTicketHistory, useTicketOptions, useAssignTicket, useSetTicketPriority,
    useSetTicketStatus, useResolveTicket, useCloseTicket, useReopenTicket, useCancelTicket,
} from '../../hooks/useSupportTickets';
import { PriorityBadge, StatusBadge } from './components/TicketTable';
import {
    getTicketActions, canReopen, priorityWasReassessed, effectivePriority,
    priorityBadgeClass, classifyWorkflowError, formatDateTime, withoutEmpty,
    ITS_AGENT_PERMISSIONS,
} from './utils';

// ─── MODAL SHELL ─────────────────────────────────────────────────────────────

function ModalShell({ title, onClose, children, footer }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-15" onClick={onClose}>
            <div
                className="bg-bg-dark rounded-lg shadow-light border border-border w-full max-w-[520px] max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-20 border-b border-border">
                    <h5 className="text-[16px]/[22px] font-semibold text-center">{title}</h5>
                </div>
                <div className="p-20 flex flex-col gap-15">{children}</div>
                <div className="flex gap-10 p-20 border-t border-border">{footer}</div>
            </div>
        </div>
    );
}

function Actions({ onClose, pending, label, danger }) {
    return (
        <>
            <button type="button" onClick={onClose} className="btn btn-outline-secondary flex-1" disabled={pending}>Cancel</button>
            <button type="submit" className={`flex-1 ${danger ? 'btn btn-danger' : 'btn btn-primary'}`} disabled={pending}>
                {pending ? <span className="flex items-center justify-center gap-5"><IconLoader2 size={14} className="animate-spin" /> Working...</span> : label}
            </button>
        </>
    );
}

const inputClass = (error) =>
    `w-full bg-bg-light border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none ${error ? 'border-danger' : 'border-border'}`;

// ─── AGENT MODALS ────────────────────────────────────────────────────────────

// assignee_id is an INTEGER Django user id — the one non-UUID id in these APIs.
function AssignModal({ ticket, orgId, onClose, onError }) {
    const assign = useAssignTicket();
    const { data: usersData } = useUsers(orgId);
    const users = useMemo(() => (Array.isArray(usersData) ? usersData : (usersData?.results || [])), [usersData]);

    const [assigneeId, setAssigneeId] = useState(ticket.assignee_id ? String(ticket.assignee_id) : '');
    const [supportGroup, setSupportGroup] = useState(ticket.support_group || '');
    const [errors, setErrors] = useState({});

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!assigneeId) { setErrors({ assignee_id: 'Choose who should work this ticket.' }); return; }

        assign.mutate(
            { id: ticket.id, orgId, data: withoutEmpty({ assignee_id: Number(assigneeId), support_group: supportGroup }) },
            {
                onSuccess: () => { toast.success('Ticket assigned.'); onClose(); },
                onError: (err) => onError(err, setErrors, onClose),
            }
        );
    };

    return (
        <form onSubmit={handleSubmit}>
            <ModalShell title="Assign Ticket" onClose={onClose} footer={<Actions onClose={onClose} pending={assign.isPending} label="Assign" />}>
                <div>
                    <label className="block text-sm font-medium mb-5">Assignee <span className="text-danger">*</span></label>
                    <select value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)} className={inputClass(errors.assignee_id)}>
                        <option value="">Select an agent...</option>
                        {users.map(u => (
                            <option key={u.id} value={u.id}>
                                {[u.first_name, u.last_name].filter(Boolean).join(' ') || u.username || u.email}
                            </option>
                        ))}
                    </select>
                    {errors.assignee_id && <p className="text-danger text-xs mt-5">{errors.assignee_id}</p>}
                </div>
                <div>
                    <label className="block text-sm font-medium mb-5">Support Group</label>
                    <input type="text" value={supportGroup} onChange={(e) => setSupportGroup(e.target.value)}
                        placeholder="e.g. Tech Support" className={inputClass(errors.support_group)} />
                </div>
                {ticket.status === 'new' && (
                    <p className="text-xs text-text-secondary">Assigning also moves this ticket from New to Open.</p>
                )}
            </ModalShell>
        </form>
    );
}

// Sets assessed_priority. The reason is required and is shown to the requester.
function PriorityModal({ ticket, orgId, options, onClose, onError }) {
    const setPriority = useSetTicketPriority();
    const [priority, setPriorityValue] = useState(effectivePriority(ticket) || '');
    const [reason, setReason] = useState('');
    const [errors, setErrors] = useState({});

    const handleSubmit = (e) => {
        e.preventDefault();
        const next = {};
        if (!priority) next.priority = 'Choose a priority.';
        if (!reason.trim()) next.reason = 'Explain why the priority is changing — the requester sees this.';
        if (Object.keys(next).length) { setErrors(next); return; }

        setPriority.mutate(
            { id: ticket.id, orgId, data: { priority, reason: reason.trim() } },
            {
                onSuccess: () => { toast.success('Priority updated.'); onClose(); },
                onError: (err) => onError(err, setErrors, onClose),
            }
        );
    };

    return (
        <form onSubmit={handleSubmit}>
            <ModalShell title="Set Priority" onClose={onClose} footer={<Actions onClose={onClose} pending={setPriority.isPending} label="Set Priority" />}>
                <p className="text-xs text-text-secondary">
                    Requested by {ticket.requester_name || 'the requester'}: <strong>{ticket.requested_priority || '-'}</strong>
                </p>
                <div>
                    <label className="block text-sm font-medium mb-5">Assessed Priority <span className="text-danger">*</span></label>
                    <select value={priority} onChange={(e) => setPriorityValue(e.target.value)} className={inputClass(errors.priority)}>
                        <option value="">Select...</option>
                        {(options?.requested_priority || []).map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                    {errors.priority && <p className="text-danger text-xs mt-5">{errors.priority}</p>}
                </div>
                <div>
                    <label className="block text-sm font-medium mb-5">Reason <span className="text-danger">*</span></label>
                    <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
                        className={`${inputClass(errors.reason)} resize-none`} />
                    {errors.reason && <p className="text-danger text-xs mt-5">{errors.reason}</p>}
                </div>
            </ModalShell>
        </form>
    );
}

// Only open ⇄ pending lack a dedicated endpoint — resolve/close/reopen/cancel
// each need fields (like resolution_notes) this generic call doesn't collect.
const GENERIC_STATUS_CHOICES = ['open', 'pending'];

function StatusModal({ ticket, orgId, options, onClose, onError }) {
    const setStatus = useSetTicketStatus();
    const [status, setStatusValue] = useState('');
    // Also exclude the current status — moving to it is a no-op the server rejects.
    const statusChoices = (options?.status || [])
        .filter(o => GENERIC_STATUS_CHOICES.includes(o.value) && o.value !== ticket.status);
    const [note, setNote] = useState('');
    const [errors, setErrors] = useState({});

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!status) { setErrors({ status: 'Choose a status.' }); return; }

        setStatus.mutate(
            { id: ticket.id, orgId, data: withoutEmpty({ status, note }) },
            {
                onSuccess: () => { toast.success('Status updated.'); onClose(); },
                onError: (err) => onError(err, setErrors, onClose),
            }
        );
    };

    return (
        <form onSubmit={handleSubmit}>
            <ModalShell title="Change Status" onClose={onClose} footer={<Actions onClose={onClose} pending={setStatus.isPending} label="Update" />}>
                <div>
                    <label className="block text-sm font-medium mb-5">Status <span className="text-danger">*</span></label>
                    <select value={status} onChange={(e) => setStatusValue(e.target.value)} className={inputClass(errors.status)}>
                        <option value="">Select...</option>
                        {statusChoices.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                    {errors.status && <p className="text-danger text-xs mt-5">{errors.status}</p>}
                    <p className="text-xs text-text-secondary mt-5">
                        Only Open and Pending are available here — use Resolve, Close, Reopen or
                        Cancel for those transitions, since each needs its own required details.
                    </p>
                </div>
                <div>
                    <label className="block text-sm font-medium mb-5">Note</label>
                    <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                        className={`${inputClass(errors.note)} resize-none`} />
                </div>
            </ModalShell>
        </form>
    );
}

// A single required free-text field: resolve, reopen, close and cancel all share it.
function TextModal({ title, label, confirmLabel, danger, required, field, mutation, ticket, orgId, onClose, onError, successMessage }) {
    const [value, setValue] = useState('');
    const [errors, setErrors] = useState({});

    const handleSubmit = (e) => {
        e.preventDefault();
        if (required && !value.trim()) { setErrors({ [field]: 'This field is required.' }); return; }

        mutation.mutate(
            { id: ticket.id, orgId, data: { [field]: value.trim() } },
            {
                onSuccess: () => { toast.success(successMessage); onClose(); },
                onError: (err) => onError(err, setErrors, onClose),
            }
        );
    };

    return (
        <form onSubmit={handleSubmit}>
            <ModalShell title={title} onClose={onClose} footer={<Actions onClose={onClose} pending={mutation.isPending} label={confirmLabel} danger={danger} />}>
                <div>
                    <label className="block text-sm font-medium mb-5">
                        {label} {required && <span className="text-danger">*</span>}
                    </label>
                    <textarea value={value} onChange={(e) => setValue(e.target.value)} rows={4}
                        className={`${inputClass(errors[field])} resize-none`} />
                    {errors[field] && <p className="text-danger text-xs mt-5">{errors[field]}</p>}
                </div>
            </ModalShell>
        </form>
    );
}

// ─── PANELS ──────────────────────────────────────────────────────────────────

function InfoRow({ label, value }) {
    return (
        <div>
            <p className="text-xs text-text-secondary">{label}</p>
            <p className="text-sm mt-2 break-words">{value || '-'}</p>
        </div>
    );
}

// Server already hides these from non-agents — colour/label guard against agent mistakes.
function CommentList({ comments }) {
    if (!comments?.length) {
        return <p className="text-sm text-text-secondary">No comments yet.</p>;
    }
    return (
        <div className="flex flex-col gap-15">
            {comments.map((comment, index) => (
                <div
                    key={comment.id || index}
                    className={`rounded-[4px] px-15 py-10 border ${comment.is_internal
                        ? 'bg-amber-500/10 border-amber-500/40'
                        : 'bg-bg-light border-border'}`}
                >
                    {comment.is_internal && (
                        <p className="text-xs font-semibold text-amber-500 flex items-center gap-5 mb-5">
                            <IconLock size={12} /> Internal note — not visible to requester
                        </p>
                    )}
                    <div className="flex items-baseline justify-between gap-10 flex-wrap">
                        <p className="text-sm font-medium">{comment.author_name || comment.created_by_name || 'Unknown'}</p>
                        <p className="text-xs text-text-secondary">{formatDateTime(comment.created_at)}</p>
                    </div>
                    <p className="text-sm mt-5 whitespace-pre-wrap">{comment.body}</p>
                </div>
            ))}
        </div>
    );
}

function CommentComposer({ ticketId, orgId, isAgent, onError }) {
    const addComment = useAddTicketComment();
    const [body, setBody] = useState('');
    const [isInternal, setIsInternal] = useState(false);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!body.trim()) return;

        addComment.mutate(
            { id: ticketId, body: body.trim(), isInternal, orgId },
            {
                onSuccess: () => { setBody(''); setIsInternal(false); toast.success(isInternal ? 'Internal note added.' : 'Comment added.'); },
                onError: (err) => onError(err, null),
            }
        );
    };

    return (
        <form onSubmit={handleSubmit} className="mt-15">
            <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={3}
                placeholder={isInternal ? 'Internal note — the requester will not see this.' : 'Reply to this ticket...'}
                className={`w-full border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none resize-none ${isInternal ? 'bg-amber-500/10 border-amber-500/40' : 'bg-bg-light border-border'}`}
            />
            <div className="flex items-center justify-between gap-10 mt-10 flex-wrap">
                {isAgent ? (
                    <label className="flex items-center gap-5 text-sm cursor-pointer">
                        <input type="checkbox" checked={isInternal} onChange={(e) => setIsInternal(e.target.checked)} />
                        <IconLock size={13} className={isInternal ? 'text-amber-500' : 'text-text-secondary'} />
                        Internal note
                    </label>
                ) : <span />}
                <button type="submit" className="btn btn-primary" disabled={addComment.isPending || !body.trim()}>
                    {addComment.isPending ? 'Sending...' : isInternal ? 'Add Internal Note' : 'Send Reply'}
                </button>
            </div>
            {!isInternal && (
                <p className="text-xs text-text-secondary mt-8">This reply is emailed to the other party.</p>
            )}
        </form>
    );
}

function History({ entries }) {
    if (!entries?.length) return <p className="text-sm text-text-secondary">No history yet.</p>;
    return (
        <div className="flex flex-col gap-15">
            {entries.map((entry, index) => (
                <div key={entry.id || index} className="flex gap-10">
                    <div className="flex flex-col items-center">
                        <span className="w-[8px] h-[8px] rounded-full bg-primary mt-5" />
                        {index < entries.length - 1 && <span className="flex-1 w-[1px] bg-border mt-3" />}
                    </div>
                    <div className="flex-1 pb-5">
                        <p className="text-sm font-medium">
                            {entry.description || entry.action || 'Updated'}
                            {entry.actor_name && <span className="text-text-secondary font-normal"> — {entry.actor_name}</span>}
                        </p>
                        <p className="text-xs text-text-secondary mt-2">{formatDateTime(entry.created_at || entry.acted_at)}</p>
                    </div>
                </div>
            ))}
        </div>
    );
}

// ─── PAGE ────────────────────────────────────────────────────────────────────

export default function SupportTicketDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user, hasAnyPermission } = useAuth();
    const orgId = user?.orgId;

    // Either its_triage or its_agent unlocks all four actions equally.
    const isAgent = hasAnyPermission(ITS_AGENT_PERMISSIONS);

    const { data: ticket, isLoading, refetch } = useSupportTicket(id, orgId);
    const { data: options } = useTicketOptions();
    const { data: comments = [] } = useTicketComments(id, orgId);
    const { data: history = [] } = useTicketHistory(id, orgId);

    const updateTicket = useUpdateTicket();
    const resolveMutation = useResolveTicket();
    const closeMutation = useCloseTicket();
    const reopenMutation = useReopenTicket();
    const cancelMutation = useCancelTicket();

    const [activeModal, setActiveModal] = useState(null);
    const [editForm, setEditForm] = useState({ description: '', additional_comments: '', phone: '', troubleshooting_availability: '' });
    const [editErrors, setEditErrors] = useState({});

    const isRequester = useMemo(() => {
        if (!ticket || !user) return false;
        if (typeof ticket.is_requester === 'boolean') return ticket.is_requester;
        return String(ticket.requester ?? '') === String(user.employeeId ?? '');
    }, [ticket, user]);

    const actions = useMemo(
        () => getTicketActions(ticket, { isAgent, isRequester }),
        [ticket, isAgent, isRequester]
    );

    // While the ticket is still `new` the requester may edit exactly four fields.
    const requesterCanEdit = isRequester && ticket?.status === 'new';

    useEffect(() => {
        if (!ticket) return;
        setEditForm({
            description: ticket.description || '',
            additional_comments: ticket.additional_comments || '',
            phone: ticket.phone || '',
            troubleshooting_availability: ticket.troubleshooting_availability || '',
        });
    }, [ticket]);

    const handleError = (error, setErrors, closeModal) => {
        const { kind, fieldErrors, message } = classifyWorkflowError(error);
        if (kind === 'stale' || kind === 'not_assignee') {
            toast(message, { icon: '🔄' });
            refetch();
            closeModal?.();
            return;
        }
        setErrors?.(fieldErrors);
        toast.error(message);
    };

    const handleSaveEdit = () => {
        setEditErrors({});
        updateTicket.mutate(
            { id, orgId, data: editForm },
            {
                onSuccess: () => toast.success('Ticket updated.'),
                onError: (err) => handleError(err, setEditErrors),
            }
        );
    };

    if (isLoading) return <PageLoader />;

    if (!ticket) {
        return (
            <div className="flex flex-col min-h-screen gap-20">
                <Breadcrumb title="Support Ticket" path="Support / Ticket" />
                <div className="bg-bg-dark rounded-lg shadow-light md:p-20 p-15 text-center text-text-secondary">
                    This ticket could not be loaded.
                </div>
            </div>
        );
    }

    const closeModal = () => setActiveModal(null);
    const showReopen = actions.includes('reopen') && canReopen(ticket);

    return (
        <div className="flex flex-col min-h-screen gap-20">
            <Breadcrumb title={ticket.reference_no || 'Support Ticket'} path="Support / Ticket" />

            {/* Header */}
            <div className="bg-bg-dark rounded-lg shadow-light md:p-20 p-15">
                <div className="flex justify-between items-start flex-wrap gap-15">
                    <div className="flex items-start gap-10">
                        <button onClick={() => navigate(-1)} className="text-text-secondary hover:text-text-main mt-3">
                            <IconArrowLeft size={18} />
                        </button>
                        <div>
                            <div className="flex items-center gap-10 flex-wrap">
                                <h2 className="text-lg font-bold">{ticket.reference_no}</h2>
                                <StatusBadge ticket={ticket} />
                                <PriorityBadge ticket={ticket} />
                            </div>
                            <p className="text-sm mt-5">{ticket.summary}</p>
                            <p className="text-xs text-text-secondary mt-3">
                                Raised by {ticket.requester_name || '-'} · {formatDateTime(ticket.created_at)}
                            </p>
                        </div>
                    </div>

                    <div className="flex gap-10 flex-wrap">
                        {/* The guide is explicit: "All of these require ITS.TRIAGE or
                            ITS.AGENT" — either permission unlocks assign, set-priority,
                            status and resolve. Gated on isAgent (triage OR agent), not
                            canTriage alone, so a pure its_agent user isn't wrongly hidden
                            from assign/set-priority. */}
                        {actions.includes('assign') && isAgent && (
                            <button onClick={() => setActiveModal('assign')} className="btn btn-primary flex items-center gap-5">
                                <IconUserCheck size={16} /> Assign
                            </button>
                        )}
                        {actions.includes('reassign') && isAgent && (
                            <button onClick={() => setActiveModal('assign')} className="btn btn-outline-secondary">Reassign</button>
                        )}
                        {actions.includes('set-priority') && isAgent && (
                            <button onClick={() => setActiveModal('priority')} className="btn btn-outline-secondary">Set Priority</button>
                        )}
                        {actions.includes('status') && (
                            <button onClick={() => setActiveModal('status')} className="btn btn-outline-secondary">Change Status</button>
                        )}
                        {actions.includes('resolve') && (
                            <button onClick={() => setActiveModal('resolve')} className="btn btn-primary">Resolve</button>
                        )}
                        {actions.includes('close') && (
                            <button onClick={() => setActiveModal('close')} className="btn btn-primary">Close</button>
                        )}
                        {showReopen && (
                            <button onClick={() => setActiveModal('reopen')} className="btn btn-outline-secondary">Reopen</button>
                        )}
                        {actions.includes('cancel') && (
                            <button onClick={() => setActiveModal('cancel')} className="btn btn-outline-secondary">Cancel Ticket</button>
                        )}
                    </div>
                </div>

                {/* IT overrode the requester's priority — never let that be silent. */}
                {priorityWasReassessed(ticket) && (
                    <div className="mt-15 bg-amber-500/10 border border-amber-500/30 rounded-[4px] px-15 py-10">
                        <p className="text-sm text-amber-500 font-medium flex items-center gap-5">
                            <IconAlertTriangle size={16} />
                            Priority changed by IT: {ticket.requested_priority} →{' '}
                            <span className={`px-6 py-2 rounded-full text-xs ${priorityBadgeClass(ticket.assessed_priority)}`}>
                                {ticket.assessed_priority}
                            </span>
                        </p>
                        {ticket.priority_change_reason && (
                            <p className="text-sm mt-5">{ticket.priority_change_reason}</p>
                        )}
                    </div>
                )}

                {ticket.status === 'resolved' && ticket.resolution_notes && (
                    <div className="mt-15 bg-success/10 border border-success/30 rounded-[4px] px-15 py-10">
                        <p className="text-sm text-success font-medium">Resolution</p>
                        <p className="text-sm mt-3 whitespace-pre-wrap">{ticket.resolution_notes}</p>
                        <p className="text-xs text-text-secondary mt-5">
                            Resolved tickets close automatically after 5 days without a reply.
                        </p>
                    </div>
                )}
            </div>

            <div className="grid xl:grid-cols-3 grid-cols-1 gap-20">
                <div className="xl:col-span-2 flex flex-col gap-20">
                    {/* Details */}
                    <div className="bg-bg-dark rounded-lg shadow-light md:p-20 p-15">
                        <h3 className="text-base font-semibold mb-15">Details</h3>
                        <div className="grid md:grid-cols-3 grid-cols-2 gap-15 mb-15">
                            <InfoRow label="Request Type" value={ticket.request_type_display || ticket.request_type} />
                            <InfoRow label="Affected System" value={ticket.affected_system} />
                            <InfoRow label="Asset Tag" value={ticket.asset_tag} />
                            <InfoRow label="Users Affected" value={ticket.users_affected_display || ticket.users_affected} />
                            <InfoRow label="Work Impact" value={ticket.work_impact_display || ticket.work_impact} />
                            <InfoRow label="Workaround" value={ticket.has_workaround_display || ticket.has_workaround} />
                            <InfoRow label="Preferred Contact" value={ticket.preferred_contact_display || ticket.preferred_contact} />
                            <InfoRow label="Phone" value={ticket.phone} />
                            <InfoRow label="Office Location" value={ticket.office_location} />
                        </div>

                        {requesterCanEdit ? (
                            <div className="flex flex-col gap-15 border-t border-border pt-15">
                                <p className="text-xs text-text-secondary">
                                    While this ticket is still New you can update the description, your phone
                                    number and your availability. Once IT picks it up, add a comment instead.
                                </p>
                                <div>
                                    <label className="block text-sm font-medium mb-5">Description</label>
                                    <textarea value={editForm.description} rows={5}
                                        onChange={(e) => setEditForm(p => ({ ...p, description: e.target.value }))}
                                        className={`${inputClass(editErrors.description)} resize-none`} />
                                    {editErrors.description && <p className="text-danger text-xs mt-5">{editErrors.description}</p>}
                                </div>
                                <div className="grid md:grid-cols-2 grid-cols-1 gap-15">
                                    <div>
                                        <label className="block text-sm font-medium mb-5">Phone</label>
                                        <input type="text" value={editForm.phone}
                                            onChange={(e) => setEditForm(p => ({ ...p, phone: e.target.value }))}
                                            className={inputClass(editErrors.phone)} />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-5">Availability for troubleshooting</label>
                                        <input type="text" value={editForm.troubleshooting_availability}
                                            onChange={(e) => setEditForm(p => ({ ...p, troubleshooting_availability: e.target.value }))}
                                            className={inputClass(editErrors.troubleshooting_availability)} />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-5">Additional Comments</label>
                                    <textarea value={editForm.additional_comments} rows={2}
                                        onChange={(e) => setEditForm(p => ({ ...p, additional_comments: e.target.value }))}
                                        className={`${inputClass(editErrors.additional_comments)} resize-none`} />
                                </div>
                                <div className="flex justify-end">
                                    <button onClick={handleSaveEdit} className="btn btn-primary" disabled={updateTicket.isPending}>
                                        {updateTicket.isPending ? 'Saving...' : 'Save Changes'}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="border-t border-border pt-15 flex flex-col gap-15">
                                <InfoRow label="Description" value={ticket.description} />
                                {ticket.business_justification && (
                                    <InfoRow label="Business Justification" value={ticket.business_justification} />
                                )}
                                {ticket.additional_comments && (
                                    <InfoRow label="Additional Comments" value={ticket.additional_comments} />
                                )}
                            </div>
                        )}

                        {ticket.attachments?.length > 0 && (
                            <div className="border-t border-border pt-15 mt-15">
                                <p className="text-xs text-text-secondary flex items-center gap-5 mb-8">
                                    <IconPaperclip size={14} /> Attachments
                                </p>
                                <div className="flex flex-col gap-8">
                                    {ticket.attachments.map((file, index) => (
                                        <a
                                            key={file.id || index}
                                            href={file.file || file.url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-sm text-primary hover:underline truncate"
                                        >
                                            {file.filename || file.name || `Attachment ${index + 1}`}
                                        </a>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Conversation */}
                    <div className="bg-bg-dark rounded-lg shadow-light md:p-20 p-15">
                        <h3 className="text-base font-semibold mb-15 flex items-center gap-5">
                            <IconMessage size={18} /> Conversation
                        </h3>
                        <CommentList comments={comments} />
                        <CommentComposer ticketId={id} orgId={orgId} isAgent={isAgent} onError={handleError} />
                    </div>
                </div>

                {/* Side column */}
                <div className="flex flex-col gap-20">
                    <div className="bg-bg-dark rounded-lg shadow-light md:p-20 p-15">
                        <h3 className="text-base font-semibold mb-15">Assignment</h3>
                        <div className="flex flex-col gap-15">
                            <InfoRow label="Assigned To" value={ticket.assignee_name} />
                            <InfoRow label="Support Group" value={ticket.support_group} />
                            <InfoRow label="Requested Priority" value={ticket.requested_priority} />
                            <InfoRow label="Assessed Priority" value={ticket.assessed_priority} />
                            <InfoRow label="Required By" value={ticket.required_completion_date} />
                        </div>
                    </div>

                    <div className="bg-bg-dark rounded-lg shadow-light md:p-20 p-15">
                        <h3 className="text-base font-semibold mb-15 flex items-center gap-5">
                            <IconHistory size={18} /> History
                        </h3>
                        <History entries={history} />
                    </div>
                </div>
            </div>

            {/* Modals */}
            {activeModal === 'assign' && (
                <AssignModal ticket={ticket} orgId={orgId} onClose={closeModal} onError={handleError} />
            )}
            {activeModal === 'priority' && (
                <PriorityModal ticket={ticket} orgId={orgId} options={options} onClose={closeModal} onError={handleError} />
            )}
            {activeModal === 'status' && (
                <StatusModal ticket={ticket} orgId={orgId} options={options} onClose={closeModal} onError={handleError} />
            )}
            {activeModal === 'resolve' && (
                <TextModal
                    title="Resolve Ticket" label="Resolution notes" confirmLabel="Resolve" required
                    field="resolution_notes" mutation={resolveMutation} ticket={ticket} orgId={orgId}
                    onClose={closeModal} onError={handleError} successMessage="Ticket resolved."
                />
            )}
            {activeModal === 'close' && (
                <TextModal
                    title="Close Ticket" label="Comment" confirmLabel="Close" required={false}
                    field="comment" mutation={closeMutation} ticket={ticket} orgId={orgId}
                    onClose={closeModal} onError={handleError} successMessage="Ticket closed."
                />
            )}
            {activeModal === 'reopen' && (
                <TextModal
                    title="Reopen Ticket" label="Why does this need reopening?" confirmLabel="Reopen" required
                    field="reason" mutation={reopenMutation} ticket={ticket} orgId={orgId}
                    onClose={closeModal} onError={handleError} successMessage="Ticket reopened."
                />
            )}
            {activeModal === 'cancel' && (
                <TextModal
                    title="Cancel Ticket" label="Comment" confirmLabel="Cancel Ticket" danger required={false}
                    field="comment" mutation={cancelMutation} ticket={ticket} orgId={orgId}
                    onClose={closeModal} onError={handleError} successMessage="Ticket cancelled."
                />
            )}
        </div>
    );
}
